import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Clock,
  Award,
  Calendar,
  BookOpen,
  CheckCircle,
  UserPlus,
  Shield,
  AlertTriangle,
  Target,
} from 'lucide-react';
import { Card, Badge, Button, Skeleton, SkeletonCard, StatCard } from '@azyntor-labs/nexus-ui';
import { examsApi } from '@/api/endpoints';
import { useExamAuth } from '@/hooks/useExamAuth';
import { Exam } from '@/types';
import { toast } from '@/utils/toast';
import { formatDistanceToNow } from 'date-fns';
import { formatExamDateTime, formatExamDateShort } from '@/utils';

// Loading skeleton
const BrowseExamsSkeleton = () => (
  <div className="space-y-6 animate-in fade-in duration-300">
    <div>
      <Skeleton width={250} height={32} className="rounded-lg" />
      <Skeleton width={200} height={18} className="mt-2 rounded-lg" />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="glass-card p-5">
          <Skeleton width={80} height={14} className="rounded mb-3" />
          <Skeleton width={60} height={24} className="rounded-lg mb-2" />
          <Skeleton width="100%" height={14} className="rounded mb-1" />
          <Skeleton width="100%" height={14} className="rounded mb-1" />
          <Skeleton width="100%" height={14} className="rounded" />
        </div>
      ))}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  </div>
);

export const BrowseExamsPage = () => {
  const queryClient = useQueryClient();
  const { user } = useExamAuth();
  const [activeTab, setActiveTab] = useState<'active' | 'upcoming'>('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmEnroll, setConfirmEnroll] = useState<Exam | null>(null);

  // Fetch active exams
  const { data: activeRes, isLoading: activeLoading } = useQuery({
    queryKey: ['browse-exams-active'],
    queryFn: () => examsApi.listActive(),
    staleTime: 30000,
  });

  const activeExams: Exam[] = Array.isArray(activeRes?.data?.data) ? activeRes.data.data : [];

  // Fetch upcoming exams
  const { data: upcomingRes, isLoading: upcomingLoading } = useQuery({
    queryKey: ['browse-exams-upcoming'],
    queryFn: () => examsApi.listUpcoming(),
    staleTime: 30000,
  });

  const upcomingExams: Exam[] = Array.isArray(upcomingRes?.data?.data) ? upcomingRes.data.data : [];

  // Fetch enrolled exams for current user to show enrolled status
  const { data: enrolledRes } = useQuery({
    queryKey: ['my-attempts'],
    queryFn: () => examsApi.list({ page: 1, per_page: 200 }),
    staleTime: 30000,
  });

  // Build a set of exam IDs the user is enrolled in
  const enrolledExamIds = useMemo(() => {
    const exams: Exam[] = Array.isArray(enrolledRes?.data?.data?.data)
      ? enrolledRes.data.data.data
      : Array.isArray(enrolledRes?.data?.data)
        ? (enrolledRes.data.data as Exam[])
        : [];
    return new Set(exams.map((e) => e.id));
  }, [enrolledRes]);

  // Enrollment mutation
  const enrollMutation = useMutation({
    mutationFn: (examId: string) => examsApi.enrollStudents(examId, [user?.id || '']),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['browse-exams-active'] });
      queryClient.invalidateQueries({ queryKey: ['browse-exams-upcoming'] });
      queryClient.invalidateQueries({ queryKey: ['my-attempts'] });
      toast.success('Successfully enrolled in exam');
      setConfirmEnroll(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to enroll');
      setConfirmEnroll(null);
    },
  });

  const currentExams = activeTab === 'active' ? activeExams : upcomingExams;
  const isLoading = activeTab === 'active' ? activeLoading : upcomingLoading;

  // Filter exams by search
  const filteredExams = useMemo(() => {
    return currentExams.filter((exam) => {
      if (!searchQuery) return true;
      return (
        exam.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (exam.description || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  }, [currentExams, searchQuery]);

  const handleEnroll = (exam: Exam) => {
    if (!user?.id) {
      toast.error('You must be logged in to enroll');
      return;
    }
    setConfirmEnroll(exam);
  };

  const confirmEnrollment = () => {
    if (!confirmEnroll) return;
    enrollMutation.mutate(confirmEnroll.id);
  };

  const getStatusVariant = (status: string): 'success' | 'info' | 'warning' | 'danger' => {
    switch (status) {
      case 'active': return 'success';
      case 'published': return 'info';
      case 'scheduled': return 'warning';
      default: return 'info';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Live Now';
      case 'published': return 'Published';
      case 'scheduled': return 'Scheduled';
      default: return status;
    }
  };

  if (isLoading && activeExams.length === 0 && upcomingExams.length === 0) {
    return <BrowseExamsSkeleton />;
  }

  const enrolledCount = currentExams.filter(e => enrolledExamIds.has(e.id)).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Browse Exams</h1>
          <p className="text-white/50 mt-1 text-sm sm:text-base">Discover and enroll in available exams</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <StatCard
          title="Active Exams"
          value={activeExams.length}
          icon={<BookOpen />}
          color="teal"
          className="!p-3 sm:!p-5 [&_h3]:!text-lg sm:[&_h3]:!text-xl"
        />
        <StatCard
          title="Upcoming"
          value={upcomingExams.length}
          icon={<Calendar />}
          color="purple"
          className="!p-3 sm:!p-5 [&_h3]:!text-lg sm:[&_h3]:!text-xl"
        />
        <StatCard
          title="Enrolled"
          value={enrolledCount}
          icon={<CheckCircle />}
          color="green"
          className="col-span-2 lg:col-span-1 !p-3 sm:!p-5 [&_h3]:!text-lg sm:[&_h3]:!text-xl"
        />
      </div>

      {/* Tabs + Search */}
      <Card>
        <div className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Tabs */}
            <div className="flex gap-2">
              {(['active', 'upcoming'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                    activeTab === tab
                      ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30'
                      : 'text-white/50 hover:text-white/70 hover:bg-white/[0.04]'
                  }`}
                >
                  {tab === 'active' ? `Active (${activeExams.length})` : `Upcoming (${upcomingExams.length})`}
                </button>
              ))}
            </div>
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search exams..."
                className="input-dark w-full pl-9 text-sm"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Exam Cards Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
        </div>
      ) : filteredExams.length === 0 ? (
        <Card>
          <div className="p-8 sm:p-12 text-center">
            <BookOpen className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-white/80 mb-1">No exams found</h3>
            <p className="text-sm text-white/50">
              {searchQuery
                ? 'No exams match your search. Try a different keyword.'
                : `No ${activeTab} exams available right now.`}
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredExams.map((exam) => {
            const isEnrolled = enrolledExamIds.has(exam.id);
            const duration = exam.duration || exam.duration_minutes || 0;
            const isLive = exam.status === 'active' || (
              new Date(exam.start_time) <= new Date() && new Date(exam.end_time) >= new Date()
            );

            return (
              <Card key={exam.id} className="flex flex-col">
                <div className="p-4 sm:p-5 flex flex-col flex-1">
                  {/* Title + Status */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <h3 className="text-sm sm:text-base font-semibold text-white/90 line-clamp-2 flex-1">
                      {exam.title}
                    </h3>
                    <Badge variant={getStatusVariant(exam.status)} size="sm">
                      {getStatusLabel(exam.status)}
                    </Badge>
                  </div>

                  {/* Description */}
                  {exam.description && (
                    <p className="text-xs sm:text-sm text-white/50 line-clamp-2 mb-3">{exam.description}</p>
                  )}

                  {/* Details */}
                  <div className="space-y-2 mb-4 flex-1">
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-white/60">
                      <Clock className="w-3.5 h-3.5 shrink-0 text-teal-400/70" />
                      <span>{duration} minutes</span>
                    </div>
                    {(exam.total_marks > 0) && (
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-white/60">
                        <Award className="w-3.5 h-3.5 shrink-0 text-amber-400/70" />
                        <span>{exam.total_marks} marks (pass: {exam.passing_marks})</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-white/60">
                      <Calendar className="w-3.5 h-3.5 shrink-0 text-purple-400/70" />
                      <span>
                        {isLive
                          ? `Ends ${formatDistanceToNow(new Date(exam.end_time), { addSuffix: true })}`
                          : `Starts ${formatExamDateTime(exam.start_time)}`
                        }
                      </span>
                    </div>
                    {/* Proctoring indicator */}
                    {exam.is_proctored && (
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-white/60">
                        <Shield className="w-3.5 h-3.5 shrink-0 text-red-400/70" />
                        <span>Proctored exam</span>
                      </div>
                    )}
                  </div>

                  {/* Action */}
                  {isEnrolled ? (
                    <div className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm font-medium text-emerald-400">Enrolled</span>
                    </div>
                  ) : (
                    <Button
                      onClick={() => handleEnroll(exam)}
                      disabled={enrollMutation.isPending}
                      leftIcon={<UserPlus className="w-4 h-4" />}
                      className="w-full"
                    >
                      Enroll Now
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Confirm Enrollment Modal */}
      {confirmEnroll && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setConfirmEnroll(null)}
        >
          <div
            className="bg-[#12121a] border border-white/[0.08] rounded-xl p-5 sm:p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-white/90 mb-2">Confirm Enrollment</h3>
            <p className="text-sm text-white/60 mb-4">
              Are you sure you want to enroll in{' '}
              <span className="text-white/90 font-medium">{confirmEnroll.title}</span>?
            </p>

            {/* Exam details */}
            <Card className="mb-5">
              <div className="p-3 sm:p-4 space-y-2.5">
                <div className="flex justify-between text-sm">
                  <span className="text-white/50 flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Duration</span>
                  <span className="text-white/80 font-medium">{confirmEnroll.duration || confirmEnroll.duration_minutes || 0} min</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50 flex items-center gap-2"><Award className="w-3.5 h-3.5" /> Total Marks</span>
                  <span className="text-white/80 font-medium">{confirmEnroll.total_marks || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50 flex items-center gap-2"><Target className="w-3.5 h-3.5" /> Passing Marks</span>
                  <span className="text-white/80 font-medium">{confirmEnroll.passing_marks || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50 flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /> Starts</span>
                  <span className="text-white/80 font-medium">
                    {confirmEnroll.start_time ? formatExamDateShort(confirmEnroll.start_time) : 'TBD'}
                  </span>
                </div>
                {confirmEnroll.is_proctored && (
                  <div className="flex items-center gap-2 text-xs text-amber-400 pt-1">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    <span>This is a proctored exam — webcam &amp; microphone required</span>
                  </div>
                )}
              </div>
            </Card>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              <Button
                variant="secondary"
                onClick={() => setConfirmEnroll(null)}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmEnrollment}
                disabled={enrollMutation.isPending}
              >
                {enrollMutation.isPending ? 'Enrolling...' : 'Confirm Enrollment'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
